import { useState, useCallback } from "react";
import { ChuziApiError } from "@chuzi/shared/api";
import type { CreateStoryRequest, RealmId } from "@chuzi/shared/types";
import { t } from "@chuzi/shared/config";
import { api } from "../lib/api";
import { useSpeechInput } from "../hooks/use-speech-input";

interface CreateInlineProps {
  realm: RealmId;
  onCreated: (storyId: string, storyTitle: string) => void;
  onCancel: () => void;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(126,184,255,0.08)",
  border: "1px solid rgba(126,184,255,0.3)",
  borderRadius: 6,
  color: "#e8f0ff",
  padding: "7px 10px",
  fontSize: 13,
  outline: "none",
  fontFamily: "inherit",
};

const wildsInputStyle: React.CSSProperties = {
  ...inputStyle,
  background: "rgba(90,180,90,0.08)",
  border: "1px solid rgba(90,180,90,0.3)",
};

function defaultStoryTitle(): string {
  const now = new Date();
  const month = now.toLocaleString("en-US", { month: "long" });
  const day = now.getDate();
  const year = now.getFullYear();
  return `${month}, ${day} ${year}`;
}

export function CreateInline({ realm, onCreated, onCancel }: CreateInlineProps) {
  const [title, setTitle] = useState(defaultStoryTitle);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isWilds = realm === "wilds";
  const iStyle = isWilds ? wildsInputStyle : inputStyle;
  const accentBorder = isWilds ? "rgba(90,180,90,0.5)" : "rgba(126,184,255,0.5)";
  const accentBg = isWilds ? "rgba(90,180,90,0.35)" : "rgba(126,184,255,0.35)";
  const micColor = isWilds ? "#78c878" : "#7eb8ff";

  const handleTranscript = useCallback((text: string) => {
    setTitle(text);
  }, []);
  const { isRecording, isSupported, toggle: toggleMic } = useSpeechInput(handleTranscript, {
    continuous: false,
  });

  async function submit() {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError(t(realm, "film_title_placeholder"));
      return;
    }
    setSubmitting(true);
    setError(null);
    const body: CreateStoryRequest = { title: trimmedTitle };
    try {
      const res = await api.stories.create(body);
      onCreated(res.data.id, res.data.title);
    } catch (err) {
      if (err instanceof ChuziApiError) {
        const body = err.body as
          | { message?: string; errors?: Record<string, string[]> }
          | null;
        const firstFieldError = body?.errors
          ? Object.values(body.errors).flat()[0]
          : null;
        setError(
          firstFieldError ?? body?.message ?? `Create failed (${err.status})`
        );
      } else {
        setError(err instanceof Error ? err.message : "Create failed");
      }
      setSubmitting(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
    if (e.key === "Escape") {
      onCancel();
    }
    e.stopPropagation();
  }

  const shellStyle: React.CSSProperties = {
    position: "absolute",
    right: "calc(50% - 320px)",
    top: "50%",
    transform: "translateY(-50%)",
    width: 260,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    zIndex: 30,
    padding: 16,
    background: isWilds ? "rgba(20,40,20,0.75)" : "rgba(10,16,32,0.75)",
    border: `1px solid ${isWilds ? "rgba(90,180,90,0.3)" : "rgba(126,184,255,0.3)"}`,
    borderRadius: 10,
    backdropFilter: "blur(12px)",
  };

  return (
    <div style={shellStyle} onKeyDown={handleKeyDown}>
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 1.5,
          color: isWilds ? "rgba(160,220,160,0.6)" : "rgba(126,184,255,0.6)",
          marginBottom: -4,
        }}
      >
        {t(realm, "menu_create_story")}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={255}
          placeholder={t(realm, "film_title_placeholder")}
          style={{ ...iStyle, flex: 1 }}
          autoFocus
          onKeyDown={handleKeyDown}
        />
        {isSupported ? (
          <button
            type="button"
            onClick={() => toggleMic(title)}
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: `1px solid ${isRecording ? "rgba(255,100,100,0.6)" : `${isWilds ? "rgba(90,180,90,0.35)" : "rgba(126,184,255,0.35)"}`}`,
              background: isRecording ? "rgba(255,80,80,0.2)" : "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 200ms ease",
              animation: isRecording ? "micPulse 1.2s ease-in-out infinite" : "none",
            }}
          >
            {isRecording ? (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <rect x="3" y="3" width="8" height="8" rx="1.5" fill="#ff6464" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3Z" fill={micColor} />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke={micColor} strokeWidth="2" strokeLinecap="round" />
                <line x1="12" y1="19" x2="12" y2="23" stroke={micColor} strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        ) : null}
      </div>
      <style>{`
        @keyframes micPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,100,100,0.4); }
          50% { box-shadow: 0 0 0 6px rgba(255,100,100,0); }
        }
      `}</style>

      <div
        style={{
          fontSize: 11,
          color: "rgba(232,240,255,0.45)",
          lineHeight: 1.4,
        }}
      >
        {t(realm, "create_publish_hint")}
      </div>

      {error ? (
        <div
          style={{
            fontSize: 11,
            color: "#ff9aa2",
            background: "rgba(255,154,162,0.08)",
            border: "1px solid rgba(255,154,162,0.4)",
            borderRadius: 6,
            padding: "6px 8px",
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
        <button
          onClick={onCancel}
          disabled={submitting}
          style={{
            flex: 1,
            padding: "7px 0",
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(232,240,255,0.6)",
            background: "transparent",
            border: `1px solid ${isWilds ? "rgba(90,180,90,0.25)" : "rgba(126,184,255,0.25)"}`,
            borderRadius: 6,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t(realm, "editor_cancel")}
        </button>
        <button
          onClick={submit}
          disabled={submitting}
          style={{
            flex: 1,
            padding: "7px 0",
            fontSize: 12,
            fontWeight: 600,
            color: "#e8f0ff",
            background: accentBg,
            border: `1px solid ${accentBorder}`,
            borderRadius: 6,
            cursor: submitting ? "wait" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {submitting ? t(realm, "chuzi_ai_creating") : t(realm, "menu_create_story")}
        </button>
      </div>
    </div>
  );
}
